import { AllocationRuleDto, CarrierVisitDirection, JourneyResponse } from '@yard/app/api'
import { occupancyService } from '@yard/services/occupancy.service'

/**
 * FE port of the backend's rule-matching pipeline — `RuleFacetsExtension.Matches` plus
 * `AllocationRulePriorityHelper.GetPrioritizedRules` / `GetWinningRule`. Kept deliberately
 * faithful so every FE consumer (the accordion badge, capacity breakdown, anything else)
 * agrees with the allocation engine's `PlannedAllocationRuleId` decision.
 *
 * Intentional divergences from the backend:
 * - The deprecated `InboundCarrierType` / `OutboundCarrierType` facets are not checked.
 *   The BE is removing them in a follow-up and no FE consumer needs them today.
 */

// "order = inboundOrder ?? outboundOrder" on the backend. The FE has flat per-direction
// fields plus `*OrderId` presence markers, so we read inbound when an inbound order
// exists, otherwise outbound.
const hasInboundOrder = (journey: JourneyResponse): boolean => journey.inboundOrderId != null

const orderIsEmpty = (journey: JourneyResponse) =>
  hasInboundOrder(journey) ? journey.inboundIsEmpty : journey.outboundIsEmpty

const orderIsDangerous = (journey: JourneyResponse) =>
  hasInboundOrder(journey) ? journey.inboundIsDangerous : journey.outboundIsDangerous

const orderConsigneeId = (journey: JourneyResponse) =>
  hasInboundOrder(journey) ? journey.inboundConsigneeId : journey.outboundConsigneeId

const orderCustomerId = (journey: JourneyResponse) =>
  hasInboundOrder(journey) ? journey.inboundCustomerId : journey.outboundCustomerId

const orderIsOOG = (journey: JourneyResponse) =>
  hasInboundOrder(journey) ? journey.inboundIsOOG : journey.outboundIsOOG

/**
 * Port of `RuleFacetsExtension.Matches`. Returns true iff every non-null facet on the rule
 * matches the journey's (container, order) pair. Direction, carrierType and carrierVisitId
 * are intentionally NOT checked here — they feed `getPrioritizedRules` instead.
 */
export const facetsMatch = (rule: AllocationRuleDto, journey: JourneyResponse): boolean => {
  const facets = rule.facets
  if (!facets) return true

  const container = journey.container

  if (facets.size != null && container?.length !== facets.size) return false
  if (facets.isEmpty != null && !!orderIsEmpty(journey) !== facets.isEmpty) return false
  if (facets.isReefer != null && !!container?.isReefer !== facets.isReefer) return false
  if (facets.isDangerous != null && !!orderIsDangerous(journey) !== facets.isDangerous) return false
  if (facets.containerOperatorId != null && container?.operatorId !== facets.containerOperatorId)
    return false
  if (facets.customerId != null && orderCustomerId(journey) !== facets.customerId) return false
  if (facets.consigneeId != null && orderConsigneeId(journey) !== facets.consigneeId) return false

  if (facets.containerHeight != null) {
    if (container?.containerHeight == null) return false
    if (container.containerHeight !== facets.containerHeight) return false
  }
  if (facets.containerType != null) {
    if (container?.containerType == null) return false
    if (container.containerType !== facets.containerType) return false
  }

  if (facets.portOfDischarge != null && journey.portOfDischarge !== facets.portOfDischarge)
    return false
  if (facets.containerNumber != null && container?.containerNumber !== facets.containerNumber)
    return false
  if (facets.isOOG != null && !!orderIsOOG(journey) !== facets.isOOG) return false

  return true
}

/**
 * Port of `AllocationRulePriorityHelper.GetPrioritizedRules`. Builds the per-journey
 * evaluation order:
 *   1. Outbound carrier-visit rules for the journey's outbound visit
 *   2. Inbound carrier-visit rules for the journey's inbound visit
 *   3. Global outbound rules matching the journey's outbound carrier type
 *   4. Global inbound rules matching the journey's inbound carrier type
 * Each bucket is sorted by `priority` ascending; duplicates are dropped across buckets.
 */
export const getPrioritizedRules = (
  journey: JourneyResponse,
  rules: AllocationRuleDto[],
): AllocationRuleDto[] => {
  const seen = new Set<string>()
  const result: AllocationRuleDto[] = []

  const addSorted = (filtered: AllocationRuleDto[]) => {
    filtered
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .forEach(r => {
        if (!seen.has(r.id)) {
          seen.add(r.id)
          result.push(r)
        }
      })
  }

  if (journey.outboundCarrierVisitId != null) {
    addSorted(
      rules.filter(
        r =>
          r.facets?.carrierVisitId === journey.outboundCarrierVisitId &&
          (r.facets?.direction == null || r.facets.direction === CarrierVisitDirection.Outbound),
      ),
    )
  }

  if (journey.inboundCarrierVisitId != null) {
    addSorted(
      rules.filter(
        r =>
          r.facets?.carrierVisitId === journey.inboundCarrierVisitId &&
          (r.facets?.direction == null || r.facets.direction === CarrierVisitDirection.Inbound),
      ),
    )
  }

  if (journey.outboundCarrierType != null) {
    addSorted(
      rules.filter(
        r =>
          r.facets?.carrierVisitId == null &&
          (r.facets?.carrierType == null || r.facets.carrierType === journey.outboundCarrierType) &&
          r.facets?.direction === CarrierVisitDirection.Outbound,
      ),
    )
  }

  if (journey.inboundCarrierType != null) {
    addSorted(
      rules.filter(
        r =>
          r.facets?.carrierVisitId == null &&
          (r.facets?.carrierType == null || r.facets.carrierType === journey.inboundCarrierType) &&
          r.facets?.direction === CarrierVisitDirection.Inbound,
      ),
    )
  }

  return result
}

/**
 * Port of `AllocationRulePriorityHelper.GetWinningRule`. Returns the first prioritized rule
 * whose facets match the journey and that satisfies the optional predicate. Consumers pass
 * `rule => rule.destinations.length > 0` when they need to mirror the allocation engine's
 * `PlannedAllocationRuleId` gate.
 */
export const getWinningRule = (
  journey: JourneyResponse,
  rules: AllocationRuleDto[],
  predicate: (rule: AllocationRuleDto) => boolean = () => true,
): AllocationRuleDto | undefined =>
  getPrioritizedRules(journey, rules).find(rule => predicate(rule) && facetsMatch(rule, journey))

/**
 * Buckets journeys into the rule they are planned against using the backend's priority and
 * facet matching, plus the backend's `destinations.Count > 0` gate so the result agrees
 * with `JourneyOccupancyAggregate.PlannedAllocationRuleId`.
 *
 * Journeys that do not win any rule are returned under `'unassigned'` when they have an
 * inbound order (mirrors the "unallocated containers" UI).
 */
export function aggregateJourneysByRules(
  journeys: JourneyResponse[],
  rules: AllocationRuleDto[],
): Record<string, JourneyResponse[]> {
  const result: Record<string, JourneyResponse[]> = {}
  const assignedJourneyIds = new Set<number>()

  journeys.forEach(journey => {
    const winner = getWinningRule(journey, rules, rule => rule.destinations.length > 0)
    if (winner) {
      ;(result[winner.id] ??= []).push(journey)
      assignedJourneyIds.add(journey.id)
    }
  })

  result['unassigned'] = journeys.filter(
    journey => !!journey.inboundOrderId && !assignedJourneyIds.has(journey.id),
  )

  return result
}

export const getBlockIdForJourneyIds = async (
  journeyIds: number[],
  isForPlannedPosition?: boolean,
) => {
  const response = await Promise.all(
    journeyIds.map(journeyId => occupancyService.openJourneyPositions(journeyId)),
  )

  return journeyIds.map((journeyId, idx) => {
    const data = response[idx]?.find(j => j.journeyId === journeyId)
    let blockId: string | null = null

    if (isForPlannedPosition)
      blockId = data?.reservations[0]?.blockId ?? data?.plannedBlockId ?? null
    else blockId = data?.occupancies[0]?.blockId ?? null

    return {
      journeyId,
      blockId,
    }
  })
}
